import React from 'react'

import {
  Alert,
  Box,
  Button,
  Form,
  Radio,
  Title,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  addCompensationAction,
  removeCompensationAction,
  setKeepExistingCompensationAction,
} from '../../BulkFixWizard/actions'
import { WizardData, wizardReducer } from '../../BulkFixWizard/state'
import { CompensationEmployeeMapUI, CompensationTemplateUI, EmployeeMetaMap } from '../../types'
import ContractSummary from '../ContractSummary'
import CompensationSummaryWidget from './CompensationSummaryWidget'
import NewCompensationWizard from './NewCompensationWizard'
import { CompletedCompensationWizardData } from './NewCompensationWizard/types'

const FADE_OUT_HEIGHT = 's80'

const isCompletedStep = (data: WizardData) => {
  return data.compensations.length > 0
}

enum KeepCompensations {
  KEEP = 'keep',
  REMOVE = 'remove',
}

const CompensationsSummaryStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const {
    keepExistingCompensations,
    targetEmployees,
    allowedLegalEntities,
    allowedPayrollPolicies,
    affectedEmployeeIds,
    unaffectedEmployeeIds,
    compensations,
  } = data
  const { company, access } = useCurrentSession()

  /* ===== State helpers ===== */

  const employeeMetaMap = targetEmployees.reduce<EmployeeMetaMap>(
    (acc, e) => ({
      ...acc,
      [e.id]: e,
    }),
    {}
  )

  // Radio only works with string values
  const retain: KeepCompensations = keepExistingCompensations
    ? KeepCompensations.KEEP
    : KeepCompensations.REMOVE

  const [showCompensationEditor, setShowCompensationEditor] = React.useState(false)

  /* ===== Event handlers ===== */

  const handleKeepExistingCompensation = (shouldKeep: boolean) => {
    const newWizardState = wizardReducer(data, setKeepExistingCompensationAction(shouldKeep))
    return () => onDataChanged(newWizardState)
  }

  const handleSaveCompensation = (newCompensation: CompletedCompensationWizardData): void => {
    const action = addCompensationAction(buildCompensation(newCompensation))
    onDataChanged(wizardReducer(data, action))

    setShowCompensationEditor(false)
  }

  const handleRemoveCompensation = (index: number) => {
    const action = removeCompensationAction(index)
    onDataChanged(wizardReducer(data, action))
  }

  const handleAddCompensationEditor = () => {
    track(trackingEvents.fixAgreementInBulk.fixAgreementNewCompensation, {
      companyId: company.id,
      acessId: access.id,
      keepExistingCompensations,
    })
    setShowCompensationEditor(true)
  }

  const { navigation } = useNavigation()

  const noEligibleEmployees = !targetEmployees.length

  return (
    <Box height="full" flexDirection="row" gap="s24">
      <Box paddingX="s24" gap="s32" flexGrow maxWidth="s600">
        <Title
          title={i18n.t('employees.bulk_edit.fix_conditions.steps.compensations.title')}
          description={i18n.t('employees.bulk_edit.fix_conditions.steps.compensations.description')}
        />

        <Box gap="s24">
          {noEligibleEmployees ? (
            <Callout
              type="warning"
              description={i18n.t('employees.bulk_edit.fix_conditions.no_eligible_employees')}
              action={
                <Button.Router to={navigation.employees.list.fullPath()} type="secondary">
                  {i18n.t('employees.bulk_edit.fix_conditions.return_to_list')}
                </Button.Router>
              }
            />
          ) : (
            <>
              <Form withBackground>
                <Box width="full" gap="s16">
                  <Title
                    variant="500"
                    title={i18n.t('employees.bulk_edit.fix_conditions.title')}
                    description={i18n.t('employees.bulk_edit.fix_conditions.description')}
                  />
                  {/* "Keep compensation" field */}
                  <Box
                    gap="s12"
                    width="full"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                    }}
                  >
                    <Radio
                      name="keep_compensations"
                      description=""
                      option={{
                        label: i18n.t('employees.bulk_edit.fix_conditions.keep_yes'),
                        value: KeepCompensations.KEEP,
                        disabled: noEligibleEmployees,
                      }}
                      value={retain}
                      onChange={handleKeepExistingCompensation(true)}
                    />
                    <Radio
                      name="keep_compensations"
                      description=""
                      option={{
                        label: i18n.t('employees.bulk_edit.fix_conditions.keep_no'),
                        value: KeepCompensations.REMOVE,
                        disabled: noEligibleEmployees,
                      }}
                      value={retain}
                      onChange={handleKeepExistingCompensation(false)}
                    />
                  </Box>
                </Box>
              </Form>

              {retain === KeepCompensations.REMOVE && (
                <Alert color="error">
                  {i18n.t(
                    'employees.bulk_edit.fix_conditions.steps.compensations.remove_compensations_warning'
                  )}
                </Alert>
              )}

              <Button iconName="add" type="secondary" onClick={() => handleAddCompensationEditor()}>
                {i18n.t(
                  'employees.bulk_edit.fix_conditions.steps.compensations.add_compensation_button'
                )}
              </Button>
            </>
          )}

          {compensations.length > 0 && (
            <Box gap="s16" paddingBottom={FADE_OUT_HEIGHT}>
              {compensations.map(({ employeeIds, compensationTemplate }, index) => {
                return (
                  <Box key={index}>
                    <CompensationSummaryWidget
                      targetEmployees={employeeMetaMap}
                      {...{ employeeIds, compensationTemplate }}
                      onRemove={handleRemoveCompensation}
                      removalIndex={index}
                    />
                  </Box>
                )
              })}
            </Box>
          )}
        </Box>
        {showCompensationEditor && (
          <NewCompensationWizard
            meta={{
              targetEmployees: employeeMetaMap,
              allowedLegalEntities,
              allowedPayrollPolicies,
            }}
            initialData={{}}
            onClose={() => setShowCompensationEditor(false)}
            onSubmit={handleSaveCompensation}
          />
        )}
      </Box>

      {compensations.length > 0 && (
        <Box gap="s24" height="s200" width="s360">
          <ContractSummary
            targetEmployees={employeeMetaMap}
            affectedEmployeeIds={[...affectedEmployeeIds]}
            unaffectedEmployeeIds={[...unaffectedEmployeeIds]}
          />
        </Box>
      )}
      {/* A basic fade effect */}
      <Box
        height={FADE_OUT_HEIGHT}
        style={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          // Hack to avoid fading the scroll bar
          right: '20px',
          background: `linear-gradient(to bottom, transparent 0%, #fff 100%)`,
          zIndex: '20',
        }}
      ></Box>
    </Box>
  )
}

/* ===== Utilities ===== */

function buildCompensation(
  newCompensation: CompletedCompensationWizardData
): CompensationEmployeeMapUI {
  const {
    // Meta
    allowedLegalEntities,
    allowedPayrollPolicies,
    legalEntityId,
    policyId,
    taxonomyName,

    // DTO fields
    affectedEmployeeIds,
    contractsTaxonomyId,
    description,
    compensationType,
    amount,
    unit,
    syncWithSupplements,
    recurrenceCount,
    startsOn,
    recurrence,
    firstPaymentOn,
    calculation,
    timeCondition,
    minimumAmountOfHours,
  } = newCompensation

  // Should never be undefined
  const legalEntityName = allowedLegalEntities.find((e) => e.id === legalEntityId)?.name ?? ''
  const policyName = allowedPayrollPolicies.find((p) => p.id === policyId)?.name ?? ''

  const builtCompensation: CompensationTemplateUI = {
    // Meta fields
    legalEntityName,
    policyName,
    taxonomyName,

    // DTO fields
    contractsTaxonomyId,
    description,
    compensationType,
    amount,
    unit,
    syncWithSupplements,
    recurrenceCount,
    startsOn,
    recurrence,
    firstPaymentOn,
    calculation,
    timeCondition,
    minimumAmountOfHours,
  }
  return {
    employeeIds: affectedEmployeeIds,
    compensationTemplate: builtCompensation,
  }
}

/* ===== Exports ===== */

export const addCompensationsStep = (): WizardStep<WizardData> => ({
  title: i18n.t('employees.bulk_edit.fix_conditions.steps.compensations.step_name'),
  nextText: i18n.t('employees.bulk_edit.fix_conditions.steps.compensations.next_title'),
  render: (props) => <CompensationsSummaryStep {...props} />,
  isCompleted: (data) => isCompletedStep(data),
})
